import { FC, useEffect, useState } from 'react';

import { Icon } from '@purpur/library/icon';
import { arrowRight } from '@purpur/library/icon/assets/arrow-right';

import { getSvgIcon } from '@telialt/common-icon/src';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';
import { isMobileRes } from '@telialt/utils/src/responsive';

import { CtaProvider } from '../CtaProvider/CtaProvider';
import { QuickLinkCardData } from './model';
import {
    QuickLinkCardChildLink,
    QuickLinkCardChildLinksContainer,
    QuickLinkCardContainer,
    QuickLinkCardContent,
    QuickLinkCardContentHead,
    QuickLinkCardDescriptionContainer,
    QuickLinkCardExpandChevron,
    QuickLinkCardImage,
    QuickLinkCardImageContainer,
    QuickLinkCardLinkIcon,
    QuickLinkCardLinkIconImage,
    QuickLinkCardTitle,
    QuickLinkCardWrapper,
    StyledAnimateHeight,
} from './QuickLinkCard.styles';
import { transformQuickLinkCardToCta } from './transformers';

type Props = {
    card: QuickLinkCardData;
};

export const QuickLinkCard: FC<Props> = ({ card }) => {
    const { colorVariant, displayVariant, title, image, iconImage, icon, url, description, links } = card;
    const cta = transformQuickLinkCardToCta(card);

    const [isMobileExpanded, setIsMobileExpanded] = useState(true);
    const [isMobile, setIsMobile] = useState<boolean>();
    const isExpandable = !!isMobile && !!links?.length;

    useEffect(() => {
        const updateIsMobile = () => {
            setIsMobile(isMobileRes());
        };

        window.addEventListener('resize', updateIsMobile);
        updateIsMobile();

        return () => window.removeEventListener('resize', updateIsMobile);
    }, []);

    const handleContainerClick = () => {
        if (!isExpandable) {
            return;
        }
        setIsMobileExpanded((isExpanded) => !isExpanded);
    };

    const elementProps = !links?.length
        ? { 'data-element': 'Link', 'data-element-name': title, 'data-element-color-option': colorVariant }
        : undefined;

    const cardContent = (
        <>
            {image && (
                <QuickLinkCardImageContainer>
                    <QuickLinkCardImage src={image.src} alt={image.alt} width={image.width} height={image.height} />
                </QuickLinkCardImageContainer>
            )}
            <QuickLinkCardContent>
                <QuickLinkCardContentHead>
                    {iconImage && <QuickLinkCardLinkIconImage src={iconImage} alt="" />}
                    {icon && <QuickLinkCardLinkIcon svg={getSvgIcon(icon)} size="md" />}
                    <QuickLinkCardTitle>{title}</QuickLinkCardTitle>
                    {url && <Icon svg={arrowRight} size="sm" />}
                    {isExpandable && <QuickLinkCardExpandChevron size="sm" $isExpanded={isMobileExpanded} />}
                </QuickLinkCardContentHead>
                {description && (
                    <QuickLinkCardDescriptionContainer>
                        <RichTextContent richText={description} />
                    </QuickLinkCardDescriptionContainer>
                )}
            </QuickLinkCardContent>
        </>
    );

    return (
        <CtaProvider cta={cta}>
            {({ href, onClick }) => (
                <QuickLinkCardWrapper
                    $colorVariant={colorVariant}
                    $displayVariant={displayVariant}
                    $isExpandable={isExpandable}
                    $isExpanded={isMobileExpanded}
                    as={(href && 'a') || (onClick && 'button') || 'div'}
                    href={href}
                    onClick={onClick}
                    data-section={!!links?.length ? 'Card With Links' : 'Link'}
                    data-section-name={title}
                    data-section-display-option={displayVariant}
                    data-section-color-option={colorVariant}
                    {...elementProps}
                >
                    {isExpandable ? (
                        <QuickLinkCardContainer
                            as="button"
                            type="button"
                            onClick={handleContainerClick}
                            aria-controls={`quick-link-child-links-${card.id}`}
                            aria-expanded={isMobileExpanded}
                        >
                            {cardContent}
                        </QuickLinkCardContainer>
                    ) : (
                        <QuickLinkCardContainer>{cardContent}</QuickLinkCardContainer>
                    )}

                    {!!links?.length && (
                        <StyledAnimateHeight
                            height={isMobileExpanded ? 'auto' : 0}
                            duration={500}
                            disableDisplayNone={false}
                            aria-hidden={isMobile && !isMobileExpanded}
                            id={`quick-link-child-links-${card.id}`}
                        >
                            <QuickLinkCardChildLinksContainer
                                $isForcedCollapsed={typeof isMobile === 'undefined' && !isMobileExpanded}
                            >
                                {links.map((childLink) => (
                                    <li key={childLink.id}>
                                        <QuickLinkCardChildLink
                                            href={childLink.url}
                                            data-element="Link"
                                            data-element-name={childLink.title}
                                        >
                                            <span>{childLink.title}</span>
                                            <Icon svg={arrowRight} size="xs" />
                                        </QuickLinkCardChildLink>
                                    </li>
                                ))}
                            </QuickLinkCardChildLinksContainer>
                        </StyledAnimateHeight>
                    )}
                </QuickLinkCardWrapper>
            )}
        </CtaProvider>
    );
};
